import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  UserIcon,
  BuildingOfficeIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

interface Verification {
  id: string;
  userId: string;
  type: 'identity' | 'business';
  userName?: string;
  userEmail?: string;
  companyName?: string;
  contactEmail?: string;
  status: 'pending' | 'approved' | 'rejected';
  documents: string[];
  submittedAt: Date;
  reviewedBy?: string;
  notes?: string;
}

interface VerificationSummary {
  pending: number;
  approved: number;
  rejected: number;
  identityVerifications: number;
  businessVerifications: number;
}

const VerificationQueue: React.FC = () => {
  const { adminUser, hasPermission } = useAdmin();
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [summary, setSummary] = useState<VerificationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('pending');

  useEffect(() => {
    fetchVerifications();
  }, []);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getVerifications();
      setVerifications(response.data?.verifications || []);
      setSummary(response.data?.summary || null);
    } catch (error: any) {
      console.error('Failed to fetch verifications:', error);
      toast.error('Failed to load verification data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (verificationId: string, newStatus: 'approved' | 'rejected', notes?: string) => {
    try {
      // This would call a real API endpoint
      toast.success(`Verification ${newStatus} successfully`);
      fetchVerifications(); // Refresh data
    } catch (error: any) {
      toast.error(`Failed to ${newStatus} verification`);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, label: 'Pending' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircleIcon, label: 'Rejected' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const typeConfig = {
      identity: { color: 'bg-blue-100 text-blue-800', icon: UserIcon, label: 'Identity' },
      business: { color: 'bg-purple-100 text-purple-800', icon: BuildingOfficeIcon, label: 'Business' }
    };
    
    const config = typeConfig[type as keyof typeof typeConfig];
    if (!config) return null;
    
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const filteredVerifications = verifications.filter(verification => {
    const typeMatch = selectedType === 'all' || verification.type === selectedType;
    const statusMatch = selectedStatus === 'all' || verification.status === selectedStatus;
    return typeMatch && statusMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Verification Queue</h1>
          <p className="mt-2 text-sm text-gray-700">
            Review and process identity and business verification requests
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.pending}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Approved</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.approved}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Rejected</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.rejected}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Identity</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.identityVerifications}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BuildingOfficeIcon className="h-6 w-6 text-purple-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Business</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.businessVerifications}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                Type:
              </label>
              <select
                id="type"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Types</option>
                <option value="identity">Identity</option>
                <option value="business">Business</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status:
              </label>
              <select
                id="status"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Verifications List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredVerifications.map((verification) => (
            <li key={verification.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {getTypeBadge(verification.type)}
                        {getStatusBadge(verification.status)}
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {verification.type === 'identity' 
                            ? verification.userName 
                            : verification.companyName}
                        </p>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-500 space-x-4">
                        <span>
                          {verification.type === 'identity' 
                            ? verification.userEmail 
                            : verification.contactEmail}
                        </span>
                        <span>
                          Submitted {new Date(verification.submittedAt).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="mt-2 flex items-center space-x-2">
                        <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          {verification.documents.length} document(s) submitted
                        </span>
                      </div>
                      
                      {verification.notes && (
                        <div className="mt-2 text-sm text-gray-600">
                          <strong>Notes:</strong> {verification.notes}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="ml-2 flex-shrink-0 flex space-x-2">
                    {verification.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleStatusChange(verification.id, 'approved')}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleStatusChange(verification.id, 'rejected')}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Reject
                        </button>
                      </>
                    )}
                    <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      <EyeIcon className="h-4 w-4 mr-1" />
                      Review
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {filteredVerifications.length === 0 && (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No verifications found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No verification requests match the current filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default VerificationQueue;